import { FC, useState } from "react"
import { useParams } from "react-router-dom"
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import { Sync } from "@mui/icons-material"
import { useTitle } from "@/core/hooks"
import { GoBack, SnackbarAlert } from "@/core/components"
import { useGetBotQuery, useRefreshProfileMutation } from "../store"
import {
  GuestBotPhotoManager,
  UpdateGuestBotForm,
  UpdateGuestBotProfileForm,
} from "../components/forms"
import { ResponsesEditor } from "../components/responses"
import { LogsTable } from "../components/tables"
import { useGuestBotWriteAccess } from "../hooks"

const TAB_KEYS = ["settings", "bindings", "logs"] as const
type TabKey = (typeof TAB_KEYS)[number]
const TAB_LABELS: Record<TabKey, string> = {
  settings: "Настройки",
  bindings: "Привязки",
  logs: "Логи",
}

export const GuestBotPage: FC = () => {
  const { id = "" } = useParams<{ id: string }>()
  const {
    data: bot,
    isLoading,
    isError,
  } = useGetBotQuery({
    pathParams: { id },
  })
  const [activeTab, setActiveTab] = useState<TabKey>("settings")
  const canWrite = useGuestBotWriteAccess()
  const [refreshProfile, { isLoading: isRefreshing }] =
    useRefreshProfileMutation()
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  useTitle(bot ? `Бот ${bot.username ?? bot.botId}` : "Гостевой бот")

  const handleRefresh = async () => {
    try {
      await refreshProfile({ pathParams: { id } }).unwrap()
      setSnackbar({
        variant: "success",
        message: "Данные бота синхронизированы из Telegram",
      })
    } catch {
      setSnackbar({
        variant: "error",
        message: "Не удалось синхронизировать данные бота",
      })
    }
  }

  if (isLoading) return <LinearProgress />
  if (isError || !bot)
    return (
      <Container maxWidth={false}>
        <GoBack />
        <Typography mt={2}>Бот не найден.</Typography>
      </Container>
    )

  return (
    <Container maxWidth={false}>
      <GoBack />
      <Box
        my={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Typography variant="h5">{bot.username ?? bot.botId}</Typography>
        {canWrite && (
          <Button
            startIcon={isRefreshing ? <CircularProgress size={18} /> : <Sync />}
            disabled={isRefreshing}
            onClick={handleRefresh}
          >
            Синхронизировать из Telegram
          </Button>
        )}
      </Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v as TabKey)}
        sx={{ mb: 2 }}
      >
        {TAB_KEYS.map((key) => (
          <Tab key={key} value={key} label={TAB_LABELS[key]} />
        ))}
      </Tabs>
      {activeTab === "settings" && (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={6} lg={4}>
            <UpdateGuestBotForm bot={bot} />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <UpdateGuestBotProfileForm bot={bot} />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <GuestBotPhotoManager botId={bot.id} />
          </Grid>
        </Grid>
      )}
      {activeTab === "bindings" && <ResponsesEditor bot={bot} />}
      {activeTab === "logs" && <LogsTable botId={id} />}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </Container>
  )
}
